import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const SecondaryNavbar = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClimateImpactsOpen, setIsClimateImpactsOpen] = useState(false);
  const [isClimateCropsOpen, setIsClimateCropsOpen] = useState(false);

  const climateRef = useRef<HTMLDivElement>(null);
  const cropsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        climateRef.current &&
        !climateRef.current.contains(event.target as Node)
      ) {
        setIsClimateImpactsOpen(false);
      }
      if (
        cropsRef.current &&
        !cropsRef.current.contains(event.target as Node)
      ) {
        setIsClimateCropsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getClimateImpactsTitle = () => {
    if (location.pathname === "/dashboard/impacts") return "CROPS PRODUCTION";
    if (location.pathname === "/dashboard/economics") return "ECONOMICS";
    return "CLIMATE IMPACTS";
  };

  const getCropsAnalysisTitle = () => {
    if (location.pathname === "/dashboard/risk-indices-2") {
      const searchParams = new URLSearchParams(location.search);
      const tab = searchParams.get("tab");
      if (tab === "risk-based") return "RISK-BASED CROPS ANALYSIS";
      return "CROPS IMPACT ANALYSIS";
    }
    return "CROPS ANALYSIS";
  };

  const getCurrentTitle = () => {
    switch (location.pathname) {
      case "/dashboard/overview":
        return "OVERVIEW";
      case "/dashboard/profile":
        return "CLIMATE PROFILE";
      case "/dashboard/stressors":
        return "CLIMATE STRESSORS";
      case "/dashboard/risk-indices":
        return "CLIMATE RISK INDEX";
      case "/dashboard/risk-indices-2":
        return getCropsAnalysisTitle();
      case "/dashboard/adaptation-options":
        return "ADAPTATION OPTIONS";
      case "/dashboard/impacts":
        return "CLIMATE IMPACT";
      case "/dashboard/economics":
        return "CLIMATE IMPACT";
      default:
        return "";
    }
  };

  return (
    <nav
      className="bg-green-200 text-white relative z-40 mt-0 border-t-0"
      style={{ overflow: "visible" }}
    >
      <div className="container max-w-7xl mx-auto px-4">
        {/* Version Desktop */}
        <div className="hidden md:flex items-center h-12 w-full">
          {/* Links Desktop avec distribution équitable de l'espace */}
          <div className="flex items-stretch flex-grow h-full w-full">
            <Link
              to="/dashboard/overview"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/overview"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              OVERVIEW
            </Link>

            <Link
              to="/dashboard/stressors"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/stressors"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              CLIMATE STRESSORS
            </Link>

            <Link
              to="/dashboard/profile"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/profile"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              CLIMATE PROFILE
            </Link>

            <div className="relative min-h-full flex-1" ref={climateRef}>
              <button
                onClick={() => setIsClimateImpactsOpen(!isClimateImpactsOpen)}
                className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1 space-x-1
                  ${
                    location.pathname === "/dashboard/impacts" ||
                    location.pathname === "/dashboard/economics"
                      ? "bg-white text-green-200"
                      : "text-white hover:text-lime-100"
                  }`}
              >
                <span>{getClimateImpactsTitle()}</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${
                    isClimateImpactsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isClimateImpactsOpen && (
                <div className="absolute top-full left-0 bg-white shadow-lg border border-gray-200 rounded-md min-w-48 z-[9999]">
                  <Link
                    to="/dashboard/impacts"
                    onClick={() => setIsClimateImpactsOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    CROPS PRODUCTION
                  </Link>
                  <Link
                    to="/dashboard/economics"
                    onClick={() => setIsClimateImpactsOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    ECONOMICS
                  </Link>
                </div>
              )}
            </div>

            {/* 
            <Link
              to="/dashboard/risk-indices"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/risk-indices"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              CLIMATE RISK INDEX
            </Link>
            */}

            <div className="relative min-h-full flex-1" ref={cropsRef}>
              <button
                onClick={() => setIsClimateCropsOpen(!isClimateCropsOpen)}
                className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full w-full flex items-center justify-center flex-1 space-x-1
                  ${
                    location.pathname === "/dashboard/risk-indices-2"
                      ? "bg-white text-green-200"
                      : "text-white hover:text-lime-100"
                  }`}
              >
                <span>{getCropsAnalysisTitle()}</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${
                    isClimateCropsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isClimateCropsOpen && (
                <div className="absolute top-full left-0 bg-white shadow-lg border border-gray-200 rounded-md min-w-48 z-[9999]">
                  <Link
                    to="/dashboard/risk-indices-2?tab=crops-impact"
                    onClick={() => setIsClimateCropsOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    CROPS IMPACT ANALYSIS
                  </Link>
                  <Link
                    to="/dashboard/risk-indices-2?tab=risk-based"
                    onClick={() => setIsClimateCropsOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    RISK-BASED CROPS ANALYSIS
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/dashboard/adaptation-options"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/adaptation-options"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              ADAPTATION OPTIONS
            </Link>
          </div>
        </div>

        {/* Version Mobile - reste inchangée */}
        <div className="md:hidden">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center space-x-2 opacity-0">
              <button className="p-2" disabled>
                <span className="text-xl">&lt;</span>
              </button>
            </div>

            <div className="flex-grow text-center text-sm font-semibold">
              {getCurrentTitle()}
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-green-500 hover:text-green-400"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMenuOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={() => setIsMenuOpen(false)}
              />
              <div className="absolute left-0 right-0 bg-green-950/95 backdrop-blur-sm border-t border-green-800 z-50 shadow-lg">
                <div className="flex flex-col px-4 py-2">
                  <Link
                    to="/dashboard/overview"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/overview"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    OVERVIEW
                  </Link>

                  <Link
                    to="/dashboard/stressors"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/stressors"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    CLIMATE STRESSORS
                  </Link>

                  <Link
                    to="/dashboard/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/profile"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    CLIMATE PROFILE
                  </Link>

                  <div className="border-b border-green-800/50">
                    <button
                      onClick={() =>
                        setIsClimateImpactsOpen(!isClimateImpactsOpen)
                      }
                      className={`w-full py-3 text-sm flex items-center justify-between
                        ${
                          location.pathname === "/dashboard/impacts" ||
                          location.pathname === "/dashboard/economics"
                            ? "text-white font-semibold"
                            : "text-white/90 hover:text-white"
                        }`}
                    >
                      <span>{getClimateImpactsTitle()}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          isClimateImpactsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isClimateImpactsOpen && (
                      <div className="pl-4 pb-2">
                        <Link
                          to="/dashboard/impacts"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsClimateImpactsOpen(false);
                          }}
                          className="block py-2 text-sm text-white/80 hover:text-white"
                        >
                          Crops production
                        </Link>
                        <Link
                          to="/dashboard/economics"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsClimateImpactsOpen(false);
                          }}
                          className="block py-2 text-sm text-white/80 hover:text-white"
                        >
                          Economics
                        </Link>
                      </div>
                    )}
                  </div>

                  <div className="border-b border-green-800/50">
                    <button
                      onClick={() => setIsClimateCropsOpen(!isClimateCropsOpen)}
                      className={`w-full py-3 text-sm flex items-center justify-between
                        ${
                          location.pathname === "/dashboard/risk-indices-2"
                            ? "text-white font-semibold"
                            : "text-white/90 hover:text-white"
                        }`}
                    >
                      <span>{getCropsAnalysisTitle()}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          isClimateCropsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isClimateCropsOpen && (
                      <div className="pl-4 pb-2">
                        <Link
                          to="/dashboard/risk-indices-2?tab=crops-impact"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsClimateCropsOpen(false);
                          }}
                          className="block py-2 text-sm text-white/80 hover:text-white"
                        >
                          Crops Impact Analysis
                        </Link>
                        <Link
                          to="/dashboard/risk-indices-2?tab=risk-based"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsClimateCropsOpen(false);
                          }}
                          className="block py-2 text-sm text-white/80 hover:text-white"
                        >
                          Risk-Based Crops Analysis
                        </Link>
                      </div>
                    )}
                  </div>

                  <Link
                    to="/dashboard/adaptation-options"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/adaptation-options"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    ADAPTATION OPTIONS
                  </Link>
                </div>

                <div className="p-4 border-t border-green-800/50">
                  <input
                    type="search"
                    placeholder="Search..."
                    className="w-full px-3 py-2 rounded-full text-sm bg-white/10 border border-green-800/50 text-white placeholder-green-400 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default SecondaryNavbar;
